import React, { useState, useMemo } from 'react';
import { useApp } from '../contexts/AppContext';
import { fmtDatum } from '../utils/datum';

const PAUZE_START = 12 * 60 + 30;
const PAUZE_EIND = 13 * 60;

function tijdNaarMinuten(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function berekenUrenMetPauze(startTijd, eindTijd) {
  if (!startTijd || !eindTijd) return 0;
  const start = tijdNaarMinuten(startTijd);
  const eind = tijdNaarMinuten(eindTijd);
  if (eind <= start) return 0;
  const brutoMin = eind - start;
  const pauzeTrekt = start < PAUZE_START && eind > PAUZE_EIND;
  const overlapStart = Math.max(start, PAUZE_START);
  const overlapEind = Math.min(eind, PAUZE_EIND);
  const pauzeOverlap = pauzeTrekt ? Math.max(0, overlapEind - overlapStart) : 0;
  return (brutoMin - pauzeOverlap) / 60;
}

function LeaveOverview() {
  const {
    currentUser, verlofaanvragen, verlofTypes, isAdmin, isManager, canManage,
    updateVerlofStatus, wijzigVerlofaanvraag, betermeldVerlof,
    medewerkers, feestdagen, VERLOF_FACTOR,
  } = useApp();
  const [filter, setFilter] = useState('alle');
  const [filterMw, setFilterMw] = useState('alle');
  const [filterType, setFilterType] = useState('alle');
  const [filterVan, setFilterVan] = useState('');
  const [filterTot, setFilterTot] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [beterItem, setBeterItem] = useState(null);
  const [beterForm, setBeterForm] = useState({ EindDatum: '', EindTijd: '', isDagdeel: false });
  const [saving, setSaving] = useState(false);
  const [sortCol, setSortCol] = useState('datum'); // datum | type | medewerker | uren | status
  const [sortDir, setSortDir] = useState('desc'); // asc | desc

  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(col === 'datum' ? 'desc' : 'asc');
    }
  };

  const sortIndicator = (col) => sortCol === col ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ↕';

  const statusOpties = [
    { key: 'ingediend', text: 'Ingediend', color: '#FFB900' },
    { key: 'goedgekeurd', text: 'Goedgekeurd', color: '#107C10' },
    { key: 'afgekeurd', text: 'Afgekeurd', color: '#D13438' },
    { key: 'ingetrokken', text: 'Ingetrokken', color: '#767676' },
  ];

  // Pure manager (not admin): show full list with approve capability
  // Manager restriction: only 1 month back
  const isManagerOnly = isManager && !isAdmin;

  let basisAanvragen = canManage ? verlofaanvragen : verlofaanvragen.filter(v => v.medewerkerId === currentUser.id);

  // Manager: filter to max 1 month in the past
  if (isManagerOnly) {
    const eenMaandGeleden = new Date();
    eenMaandGeleden.setMonth(eenMaandGeleden.getMonth() - 1);
    const minDatum = eenMaandGeleden.toISOString().split('T')[0];
    basisAanvragen = basisAanvragen.filter(v => v.StartDatum >= minDatum || v.Status === 'ingediend' || v.isOpenZiek);
  }

  // Open ziekmeldingen bovenaan
  basisAanvragen = [...basisAanvragen].sort((a, b) => {
    if (a.isOpenZiek && !b.isOpenZiek) return -1;
    if (!a.isOpenZiek && b.isOpenZiek) return 1;
    return (b.StartDatum || '').localeCompare(a.StartDatum || '');
  });
  const filtered = (filter === 'alle' ? basisAanvragen : basisAanvragen.filter(v => v.Status === filter))
    .filter(v => filterMw === 'alle' || v.medewerkerId === filterMw)
    .filter(v => filterType === 'alle' || (v.Type || v.type) === filterType)
    .filter(v => !filterVan || (v.StartDatum || '') >= filterVan)
    .filter(v => !filterTot || (v.StartDatum || '') <= filterTot)
    .sort((a, b) => {
      // Open ziekmeldingen altijd bovenaan
      if (a.isOpenZiek && !b.isOpenZiek) return -1;
      if (!a.isOpenZiek && b.isOpenZiek) return 1;
      const dir = sortDir === 'asc' ? 1 : -1;
      const getMwNaam = (v) => (medewerkers.find(m => m.Id === v.medewerkerId)?.Naam || '');
      switch (sortCol) {
        case 'medewerker': return dir * getMwNaam(a).localeCompare(getMwNaam(b));
        case 'type': return dir * ((a.Type || '').localeCompare(b.Type || ''));
        case 'uren': return dir * ((a.Uren || 0) - (b.Uren || 0));
        case 'status': return dir * ((a.Status || '').localeCompare(b.Status || ''));
        case 'datum':
        default: return dir * ((a.StartDatum || '').localeCompare(b.StartDatum || ''));
      }
    });

  const openZiekCount = basisAanvragen.filter(v => v.isOpenZiek).length;

  const handleStatusUpdate = async (id, status) => {
    setProcessing(id);
    try {
      await updateVerlofStatus(id, status);
    } catch (err) { console.error(err); } finally { setProcessing(null); }
  };

  // Can edit: ingediend altijd, goedgekeurd ook (voor admin/manager + eigen)
  const canEdit = (v) => {
    if (v.isOpenZiek) return false; // open ziek: gebruik betermelden
    if (v.Status === 'afgekeurd' || v.Status === 'ingetrokken') return false;
    if (canManage) return true;
    return v.medewerkerId === currentUser.id;
  };

  const canBetermelden = (v) => v.isOpenZiek && (canManage || v.medewerkerId === currentUser.id);

  const openEdit = (v) => {
    setEditItem(v);
    setEditForm({
      Type: v.Type || 'vakantie',
      StartDatum: v.StartDatum || '',
      EindDatum: v.EindDatum || '',
      isDagdeel: !!(v.StartTijd && v.EindTijd),
      StartTijd: v.StartTijd || '',
      EindTijd: v.EindTijd || '',
      Reden: v.Reden || '',
      Status: v.Status || 'ingediend',
    });
  };

  const berekenEditUren = () => {
    if (!editForm.StartDatum || !editForm.EindDatum) return 0;
    if (editForm.isDagdeel && editForm.StartTijd && editForm.EindTijd) {
      return Math.round(berekenUrenMetPauze(editForm.StartTijd, editForm.EindTijd) * 100) / 100;
    }
    const mw = medewerkers.find(m => m.Id === editItem?.medewerkerId);
    const dagUren = (mw?.ContractUren || 40) / 5;
    let count = 0;
    const start = new Date(editForm.StartDatum + 'T12:00:00');
    const end = new Date(editForm.EindDatum + 'T12:00:00');
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue;
      if (feestdagen.some(f => f.Datum === d.toISOString().split('T')[0])) continue;
      count++;
    }
    return Math.round(count * dagUren * 100) / 100;
  };
  const editUren = editItem ? berekenEditUren() : 0;

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const updates = {
        type: editForm.Type,
        startDatum: editForm.StartDatum,
        eindDatum: editForm.EindDatum,
        beginTijd: editForm.isDagdeel ? editForm.StartTijd : '',
        eindTijd: editForm.isDagdeel ? editForm.EindTijd : '',
        reden: editForm.Reden,
        uren: editUren,
        gedeeltelijk: editForm.isDagdeel,
      };
      if (isAdmin) updates.status = editForm.Status;
      await wijzigVerlofaanvraag(editItem.Id, updates);
      setEditItem(null);
    } catch (err) {
      alert('Wijzigen mislukt: ' + err.message);
    } finally { setSaving(false); }
  };

  const handleIntrekken = async () => {
    if (!editItem || !window.confirm('Weet je zeker dat je deze aanvraag wilt intrekken?')) return;
    setSaving(true);
    try { await updateVerlofStatus(editItem.Id, 'ingetrokken'); setEditItem(null); }
    catch (err) { console.error(err); } finally { setSaving(false); }
  };

  // === BETERMELDEN ===
  const openBetermelden = (v) => {
    setBeterItem(v);
    const today = new Date().toISOString().split('T')[0];
    setBeterForm({ EindDatum: today, EindTijd: '', isDagdeel: false });
  };

  const berekenZiekUren = () => {
    if (!beterItem || !beterForm.EindDatum) return 0;
    const mw = medewerkers.find(m => m.Id === beterItem.medewerkerId);
    const dagUren = (mw?.ContractUren || 40) / 5;
    const rooster = mw?.Werkrooster;
    const dagKeys = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
    const feestdagenSet = new Set(feestdagen.map(f => f.Datum));
    
    let totaalUren = 0;
    const start = new Date(beterItem.StartDatum + 'T12:00:00');
    const end = new Date(beterForm.EindDatum + 'T12:00:00');
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dow = d.getDay();
      const dateStr = d.toISOString().split('T')[0];
      if (dow === 0 || dow === 6) continue; // weekend
      if (feestdagenSet.has(dateStr)) continue; // feestdag
      
      const dk = dagKeys[dow];
      const isEersteDag = dateStr === beterItem.StartDatum;
      const isLaatsteDag = dateStr === beterForm.EindDatum;
      
      // Check werkrooster voor deze dag
      let dagWerkUren = dagUren;
      if (rooster && rooster[dk]) {
        const r = rooster[dk];
        let minuten = 0;
        if (r.ochtend) { const p = r.ochtend.split('-'); minuten += tijdNaarMinuten(p[1]) - tijdNaarMinuten(p[0]); }
        if (r.middag) { const p = r.middag.split('-'); minuten += tijdNaarMinuten(p[1]) - tijdNaarMinuten(p[0]); }
        dagWerkUren = minuten / 60;
      } else if (rooster && !rooster[dk]) {
        continue; // werkt niet op deze dag
      }
      
      // Eerste dag: als er een starttijd is, bereken deeltijd
      if (isEersteDag && beterItem.StartTijd) {
        const ziekVanaf = tijdNaarMinuten(beterItem.StartTijd);
        // Uren vanaf ziek tot einde werkdag (17:00 default)
        const eindWerkdag = 17 * 60;
        const restMinuten = Math.max(0, eindWerkdag - ziekVanaf);
        // Trek pauze af als van toepassing
        const pauzeAftrek = (ziekVanaf < PAUZE_START && eindWerkdag > PAUZE_EIND) ? 30 : 0;
        totaalUren += Math.max(0, (restMinuten - pauzeAftrek) / 60);
        continue;
      }
      
      // Laatste dag: als er een eindtijd is, bereken deeltijd
      if (isLaatsteDag && beterForm.isDagdeel && beterForm.EindTijd) {
        const beterTot = tijdNaarMinuten(beterForm.EindTijd);
        const startWerkdag = 8 * 60 + 30; // 08:30 default
        const ziekMinuten = Math.max(0, beterTot - startWerkdag);
        const pauzeAftrek = (startWerkdag < PAUZE_START && beterTot > PAUZE_EIND) ? 30 : 0;
        totaalUren += Math.max(0, (ziekMinuten - pauzeAftrek) / 60);
        continue;
      }
      
      totaalUren += dagWerkUren;
    }
    
    return Math.round(totaalUren * 100) / 100;
  };
  const ziekUren = beterItem ? berekenZiekUren() : 0;

  const handleBetermelden = async () => {
    if (!beterItem || !beterForm.EindDatum) return;
    setSaving(true);
    try {
      await betermeldVerlof(
        beterItem.Id,
        beterForm.EindDatum,
        beterForm.isDagdeel ? beterForm.EindTijd : null,
        ziekUren
      );
      setBeterItem(null);
    } catch (err) {
      alert('Betermelden mislukt: ' + err.message);
    } finally { setSaving(false); }
  };

  // Edit Modal
  const EditModal = () => {
    if (!editItem) return null;
    return (
      <div style={sty.overlay} onClick={() => setEditItem(null)}>
        <div style={sty.modal} className="r-modal" onClick={e => e.stopPropagation()}>
          <div style={sty.modalHeader}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>Verlofaanvraag wijzigen</h3>
            <button style={sty.closeBtn} onClick={() => setEditItem(null)}>✕</button>
          </div>
          {canManage && <div style={{ fontSize: 13, color: '#605e5c', marginBottom: 12 }}>Medewerker: <strong>{editItem.MedewerkerNaam}</strong></div>}
          <div style={sty.formGrid} className="r-label-grid">
            <label style={sty.label}>Type verlof</label>
            <select style={sty.input} value={editForm.Type} onChange={e => setEditForm(f => ({ ...f, Type: e.target.value }))}>
              {verlofTypes.map(t => <option key={t.key} value={t.key}>{t.text}</option>)}
            </select>
            <label style={sty.label}>Startdatum</label>
            <input type="date" style={sty.input} value={editForm.StartDatum} onChange={e => setEditForm(f => ({ ...f, StartDatum: e.target.value, EindDatum: f.EindDatum < e.target.value ? e.target.value : f.EindDatum }))} />
            <label style={sty.label}>Einddatum</label>
            <input type="date" style={sty.input} value={editForm.EindDatum} min={editForm.StartDatum} onChange={e => setEditForm(f => ({ ...f, EindDatum: e.target.value }))} />
            <label style={sty.label}>Met start-/eindtijd</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={editForm.isDagdeel} onChange={e => setEditForm(f => ({ ...f, isDagdeel: e.target.checked }))} />
              <span style={{ fontSize: 13, color: '#323130' }}>Specifieke tijden</span>
            </label>
            {editForm.isDagdeel && (<>
              <label style={sty.label}>Starttijd</label>
              <input type="time" style={sty.input} value={editForm.StartTijd} onChange={e => setEditForm(f => ({ ...f, StartTijd: e.target.value }))} />
              <label style={sty.label}>Eindtijd</label>
              <input type="time" style={sty.input} value={editForm.EindTijd} onChange={e => setEditForm(f => ({ ...f, EindTijd: e.target.value }))} />
            </>)}
            {(!isManagerOnly || editItem?.ingediendDoor === currentUser.id || editItem?.medewerkerId === currentUser.id) && (<>
              <label style={sty.label}>Opmerkingen</label>
              <input type="text" style={sty.input} value={editForm.Reden} placeholder="Optioneel" onChange={e => setEditForm(f => ({ ...f, Reden: e.target.value }))} />
            </>)}
            {isAdmin && (<>
              <label style={sty.label}>Status</label>
              <select style={sty.input} value={editForm.Status} onChange={e => setEditForm(f => ({ ...f, Status: e.target.value }))}>
                {statusOpties.map(s => <option key={s.key} value={s.key}>{s.text}</option>)}
              </select>
            </>)}
          </div>
          <div style={{ marginTop: 16, padding: '10px 14px', background: '#f3f2f1', borderRadius: 8, fontSize: 13 }}>
            Berekende uren: <strong>{editUren}u</strong>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'space-between' }}>
            <button style={sty.intrekkenBtn} onClick={handleIntrekken} disabled={saving}>Intrekken</button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={sty.cancelBtn} onClick={() => setEditItem(null)}>Annuleren</button>
              <button style={sty.saveBtn} onClick={handleSave} disabled={saving || !editForm.StartDatum || !editForm.EindDatum}>
                {saving ? 'Opslaan...' : 'Opslaan'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Betermelden Modal
  const BeterModal = () => {
    if (!beterItem) return null;
    return (
      <div style={sty.overlay} onClick={() => setBeterItem(null)}>
        <div style={sty.modal} className="r-modal" onClick={e => e.stopPropagation()}>
          <div style={sty.modalHeader}>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#107C10' }}>✅ Betermelden</h3>
            <button style={sty.closeBtn} onClick={() => setBeterItem(null)}>✕</button>
          </div>
          <div style={{ fontSize: 13, color: '#605e5c', marginBottom: 16 }}>
            <strong>{beterItem.MedewerkerNaam}</strong> is ziek sinds <strong>{fmtDatum(beterItem.StartDatum)}</strong>
            {beterItem.StartTijd && <> vanaf <strong>{beterItem.StartTijd}</strong></>}
          </div>
          <div style={sty.formGrid} className="r-label-grid">
            <label style={sty.label}>Beter op</label>
            <input type="date" style={sty.input} value={beterForm.EindDatum} min={beterItem.StartDatum}
              max={new Date().toISOString().split('T')[0]}
              onChange={e => setBeterForm(f => ({ ...f, EindDatum: e.target.value }))} />
            <label style={sty.label}>Deeltijd</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={beterForm.isDagdeel}
                onChange={e => setBeterForm(f => ({ ...f, isDagdeel: e.target.checked }))} />
              <span style={{ fontSize: 13 }}>Beter op een specifiek tijdstip</span>
            </label>
            {beterForm.isDagdeel && (<>
              <label style={sty.label}>Beter vanaf</label>
              <input type="time" style={sty.input} value={beterForm.EindTijd}
                onChange={e => setBeterForm(f => ({ ...f, EindTijd: e.target.value }))} />
            </>)}
          </div>
          <div style={{ marginTop: 16, padding: '14px 18px', background: '#dff6dd', borderRadius: 10, fontSize: 14, color: '#107C10' }}>
            <strong>Totaal ziekteuren: {ziekUren}u</strong>
            <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>
              Berekend op basis van werkrooster (excl. weekenden, feestdagen, niet-werkdagen)
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
            <button style={sty.cancelBtn} onClick={() => setBeterItem(null)}>Annuleren</button>
            <button style={{ ...sty.saveBtn, background: '#107C10' }} onClick={handleBetermelden}
              disabled={saving || !beterForm.EindDatum}>
              {saving ? 'Bezig...' : '✅ Betermelden'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={sty.container}>
      {/* Open ziekmelding banner */}
      {openZiekCount > 0 && (
        <div style={{ background: '#fff4ce', border: '1px solid #FFB900', borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 22 }}>🏥</span>
          <div>
            <strong style={{ color: '#7A6400' }}>{openZiekCount} open ziekmelding{openZiekCount > 1 ? 'en' : ''}</strong>
            <div style={{ fontSize: 12, color: '#a19f9d' }}>Gebruik de groene knop om beter te melden</div>
          </div>
        </div>
      )}

      <div style={sty.filterRow} className="r-filters">
        {[
          { key: 'alle', label: 'Alle', count: basisAanvragen.length },
          { key: 'ingediend', label: 'Openstaand', count: basisAanvragen.filter(v => v.Status === 'ingediend').length },
          { key: 'goedgekeurd', label: 'Goedgekeurd', count: basisAanvragen.filter(v => v.Status === 'goedgekeurd').length },
          { key: 'afgekeurd', label: 'Afgekeurd', count: basisAanvragen.filter(v => v.Status === 'afgekeurd').length },
        ].map(tab => (
          <button key={tab.key} style={{ ...sty.filterTab, ...(filter === tab.key ? sty.filterTabActive : {}) }}
            onClick={() => setFilter(tab.key)}>
            {tab.label} <span style={sty.filterCount}>{tab.count}</span>
          </button>
        ))}
        <button style={{ ...sty.filterTab, marginLeft: 'auto', border: showFilters ? '1px solid #0078D4' : '1px solid #edebe9', color: showFilters ? '#0078D4' : '#605e5c' }}
          onClick={() => setShowFilters(!showFilters)}>
          🔍 Filters {(filterMw !== 'alle' || filterType !== 'alle' || filterVan || filterTot) ? '●' : ''}
        </button>
      </div>

      {showFilters && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', padding: '12px 16px', background: '#fafafa', borderRadius: 10, border: '1px solid #edebe9' }}>
          {canManage && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#605e5c', fontWeight: 600 }}>
              Medewerker
              <select value={filterMw} onChange={e => setFilterMw(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #edebe9', fontSize: 13, minWidth: 140 }}>
                <option value="alle">Alle medewerkers</option>
                {medewerkers.filter(m => (m.Status || 'actief') === 'actief').sort((a,b) => (a.Naam||'').localeCompare(b.Naam||'')).map(m => (
                  <option key={m.Id} value={m.Id}>{m.Naam}</option>
                ))}
              </select>
            </label>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#605e5c', fontWeight: 600 }}>
            Type
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #edebe9', fontSize: 13, minWidth: 130 }}>
              <option value="alle">Alle types</option>
              {verlofTypes.map(vt => (
                <option key={vt.key} value={vt.key}>{vt.text}</option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#605e5c', fontWeight: 600 }}>
            Van
            <input type="date" value={filterVan} onChange={e => setFilterVan(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #edebe9', fontSize: 13 }} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 11, color: '#605e5c', fontWeight: 600 }}>
            Tot
            <input type="date" value={filterTot} onChange={e => setFilterTot(e.target.value)} style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid #edebe9', fontSize: 13 }} />
          </label>
          {(filterMw !== 'alle' || filterType !== 'alle' || filterVan || filterTot) && (
            <button onClick={() => { setFilterMw('alle'); setFilterType('alle'); setFilterVan(''); setFilterTot(''); }}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #D13438', background: '#fde7e9', color: '#D13438', fontSize: 12, fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-end' }}>
              ✕ Wis filters
            </button>
          )}
        </div>
      )}
      <div style={sty.tableCard} className="r-card">
        {filtered.length === 0 ? (
          <p style={sty.emptyText}>Geen verlofaanvragen gevonden.</p>
        ) : (
          <div className="r-table-wrap">
          <table style={sty.table}><thead><tr>
            {canManage && <th style={{ ...sty.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('medewerker')}>Medewerker{sortIndicator('medewerker')}</th>}
            <th style={{ ...sty.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('type')}>Type verlof{sortIndicator('type')}</th>
            <th style={{ ...sty.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('datum')}>Periode{sortIndicator('datum')}</th>
            <th style={sty.th}>Tijd</th>
            <th style={{ ...sty.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('uren')}>Uren{sortIndicator('uren')}</th>
            <th style={sty.th}>Opmerkingen</th>
            {canManage && <th style={sty.th}>Ingediend door</th>}
            <th style={{ ...sty.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
            <th style={sty.th}>Acties</th>
          </tr></thead><tbody>
            {filtered.map(v => {
              const typeInfo = verlofTypes.find(t => t.key === v.Type);
              const statusInfo = statusOpties.find(s => s.key === v.Status);
              const editable = canEdit(v);
              const beterbaar = canBetermelden(v);
              return (
                <tr key={v.Id} style={{ ...sty.tr, background: v.isOpenZiek ? '#fff8f0' : undefined }}>
                  {canManage && <td style={sty.td}><strong>{v.MedewerkerNaam}</strong></td>}
                  <td style={sty.td}>
                    <span style={{ ...sty.typeBadge, background: `${typeInfo?.color || '#767676'}15`, color: typeInfo?.color }}>
                      {typeInfo?.text || v.Type}
                    </span>
                    {v.isOpenZiek && <div style={{ fontSize: 10, color: '#D83B01', fontWeight: 600, marginTop: 2 }}>🏥 Open ziekmelding</div>}
                    {typeInfo && typeInfo.trektAfVanVerlof === false && <div style={{ fontSize: 10, color: '#107C10', marginTop: 2 }}>geen aftrek</div>}
                    {v.WijzerResultaat && (
                      <div style={{ fontSize: 10, color: '#8764B8', marginTop: 2 }} title={v.WijzerWettelijk || ''}>
                        ⚖️ {v.WijzerResultaat}
                      </div>
                    )}
                  </td>
                  <td style={sty.td}>
                    <div>{fmtDatum(v.StartDatum)}</div>
                    {v.isOpenZiek
                      ? <div style={{ fontSize: 12, color: '#D83B01', fontWeight: 600 }}>nog ziek...</div>
                      : <div style={sty.tdSub}>t/m {fmtDatum(v.EindDatum)}</div>}
                  </td>
                  <td style={sty.td}>
                    {v.StartTijd && v.EindTijd
                      ? <span style={{ fontSize: 13 }}>{v.StartTijd}–{v.EindTijd}</span>
                      : v.StartTijd && v.isOpenZiek
                        ? <span style={{ fontSize: 13 }}>vanaf {v.StartTijd}</span>
                        : <span style={{ fontSize: 12, color: '#a19f9d' }}>Hele werkdag</span>}
                  </td>
                  <td style={sty.td}>
                    {v.isOpenZiek
                      ? <span style={{ color: "#a19f9d" }}>—</span>
                      : <>
                          <div><strong>{v.Uren}u</strong></div>
                          {v.overurenGebruikt > 0 && (
                            <div style={{ fontSize: 10, color: "#E86E1C", lineHeight: 1.3 }}>
                              {v.overurenGebruikt}u overuren · {Math.max(0, v.Uren - v.overurenGebruikt)}u verlof
                            </div>
                          )}
                        </>
                    }
                  </td>
                  {!isManagerOnly && <td style={{ ...sty.td, maxWidth: 200 }}><div style={sty.truncate}>{v.Reden || '—'}</div></td>}
                  {isManagerOnly && <td style={{ ...sty.td, maxWidth: 200 }}><div style={sty.truncate}>{
                    (v.ingediendDoor === currentUser.id || v.medewerkerId === currentUser.id) ? (v.Reden || '—') : '—'
                  }</div></td>}
                  {canManage && <td style={sty.td}><span style={{ fontSize: 12, color: '#605e5c' }}>{
                      v.ingediendDoor === v.medewerkerId ? '👤 Zelf'
                      : medewerkers.find(m => m.Id === v.ingediendDoor)?.IsAdmin ? `👑 ${v.IngediendDoorNaam || 'Beheerder'}`
                      : `📋 ${v.IngediendDoorNaam || 'Manager'}`
                    }</span></td>}
                  <td style={sty.td}>
                    <span style={{ ...sty.statusBadge, background: `${statusInfo?.color}15`, color: statusInfo?.color }}>
                      {statusInfo?.text || v.Status}
                    </span>
                  </td>
                  <td style={sty.td}>
                    <div style={sty.actionRow}>
                      {beterbaar && (
                        <button style={sty.beterBtn} onClick={() => openBetermelden(v)} title="Betermelden">✅</button>
                      )}
                      {editable && (
                        <button style={sty.editBtn} onClick={() => openEdit(v)} title="Wijzigen">✏️</button>
                      )}
                      {canManage && v.Status === 'ingediend' && !v.isOpenZiek && (<>
                        <button style={sty.approveBtn} onClick={() => handleStatusUpdate(v.Id, 'goedgekeurd')} disabled={processing === v.Id} title="Goedkeuren">✓</button>
                        <button style={sty.rejectBtn} onClick={() => handleStatusUpdate(v.Id, 'afgekeurd')} disabled={processing === v.Id} title="Afkeuren">✕</button>
                      </>)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody></table>
          </div>
        )}
      </div>
      <EditModal />
      <BeterModal />
    </div>
  );
}

const sty = {
  container: { display: 'flex', flexDirection: 'column', gap: 20 },
  card: { background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterTab: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, border: '1px solid #edebe9', background: '#fff', fontSize: 13, fontWeight: 600, color: '#605e5c', cursor: 'pointer' },
  filterTabActive: { background: '#E86E1C', color: '#fff', border: '1px solid #E86E1C' },
  filterCount: { fontSize: 11, opacity: 0.7 },
  tableCard: { background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: '16px 0', maxWidth: '100%', boxSizing: 'border-box' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '14px 16px', fontSize: 12, fontWeight: 600, color: '#605e5c', borderBottom: '2px solid #edebe9', textTransform: 'uppercase', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f2f1' },
  td: { padding: '12px 16px', fontSize: 14, color: '#323130', verticalAlign: 'middle' },
  tdSub: { fontSize: 12, color: '#a19f9d' },
  truncate: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  typeBadge: { fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 6, whiteSpace: 'nowrap' },
  statusBadge: { fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap' },
  actionRow: { display: 'flex', gap: 6, alignItems: 'center' },
  editBtn: { width: 32, height: 32, borderRadius: 8, border: '1px solid #edebe9', background: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  beterBtn: { width: 32, height: 32, borderRadius: 8, border: '1px solid #107C10', background: '#dff6dd', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  approveBtn: { width: 32, height: 32, borderRadius: 8, border: 'none', background: '#dff6dd', color: '#107C10', fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  rejectBtn: { width: 32, height: 32, borderRadius: 8, border: 'none', background: '#fde7e9', color: '#D13438', fontSize: 16, fontWeight: 700, cursor: 'pointer' },
  emptyText: { fontSize: 14, color: '#a19f9d', textAlign: 'center', padding: 48 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  closeBtn: { width: 32, height: 32, borderRadius: 8, border: '1px solid #edebe9', background: '#fff', fontSize: 16, cursor: 'pointer' },
  formGrid: { display: 'grid', gridTemplateColumns: '110px 1fr', gap: '10px 12px', alignItems: 'center' },
  label: { fontSize: 13, fontWeight: 600, color: '#605e5c' },
  input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #edebe9', fontSize: 14, color: '#323130', outline: 'none', width: '100%', boxSizing: 'border-box' },
  saveBtn: { padding: '10px 24px', borderRadius: 8, border: 'none', background: '#E86E1C', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid #edebe9', background: '#fff', color: '#605e5c', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  intrekkenBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid #D13438', background: '#fff', color: '#D13438', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};

export default LeaveOverview;
